// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

/**
 * The one rendering path for text charsight did not write.
 *
 * Every artefact this tool produces quotes a payload it found. HTML-escaping that payload is the
 * obvious protection and it is the wrong threat model: escaping neutralises `< > & "` and does
 * exactly nothing to the code points charsight exists to find. Three ways that goes wrong, and all
 * three are the tool defeating itself:
 *
 *  1. **The report gets Trojan-Sourced.** A decoded payload containing U+202E, written as escaped
 *     text, still reaches the reader's browser as a live bidi control and reorders the report's own
 *     surrounding markup under UAX #9. The file you attach to a security ticket then lies to the
 *     person reading it — produced by the one tool that knows better.
 *  2. **The CSV re-smuggles.** Write the decoded tag characters into `findings.csv` as themselves
 *     and the artefact carries the payload onward, invisibly, into whatever reads it next.
 *  3. **The evidence is unsearchable.** A findings file about invisible characters that contains
 *     invisible characters cannot be grepped, and pasting a row into a ticket carries the payload
 *     with it.
 *
 * So there is exactly one function that turns untrusted text into displayable text, and every
 * surface — the reveal pane, `report.html`, `findings.csv` and the clipboard — goes through it.
 * The lossless form of a payload is the hex dump beside it, never the raw characters.
 */

import { charName, formatCodePoint } from '../data/unicode';
import { BIDI_NAMES, categoryOf, isCandidate } from '../scan/catalogue';

/**
 * Which characters have to be replaced by a token, as opposed to merely being reported.
 *
 * NOT every character charsight has something to say about. A curly quote and an em dash are
 * perfectly visible and perfectly harmless in a report — chipping them would render every quoted
 * passage as `café ‹‹U+2014›› naïve` and make the evidence unreadable, which is a real cost for no
 * benefit. What has to go is what is invisible or what reorders: those are the characters that
 * would ride into the artefact unseen, or turn the report against its reader.
 */
export function needsChipping(cp: number): boolean {
  return isCandidate(cp) && categoryOf(cp) !== 'typographic';
}

/** Guillemets, because they are unlikely to appear in the payload itself and read as a label. */
const OPEN = '‹‹';
const CLOSE = '››';

/**
 * A short, stable token for a code point charsight would flag.
 *
 * Bidi controls get their conventional abbreviation (`‹‹RLO››`) because that is what the literature
 * and every compiler warning call them. Everything else gets its code point, which is unambiguous
 * and greppable.
 */
export function chipLabel(cp: number): string {
  const bidi = BIDI_NAMES.get(cp);
  if (bidi) return bidi;
  if (cp === 0x00) return 'NUL';
  if (cp === 0x1b) return 'ESC';
  if (cp === 0x09) return 'TAB';
  return formatCodePoint(cp);
}

/** The long form, for a tooltip or a table cell: `U+200B ZERO WIDTH SPACE`. */
export function describeCodePoint(cp: number): string {
  const name = charName(cp);
  return name ? `${formatCodePoint(cp)} ${name}` : formatCodePoint(cp);
}

export interface ChipOptions {
  /** Also chip tab, newline and carriage return. Off for the reveal pane, on for CSV cells. */
  chipAsciiWhitespace?: boolean;
}

/**
 * Replace every code point charsight would flag with a visible token.
 *
 * Note it substitutes BEFORE anything reaches a DOM or a file, not after — which is also what makes
 * the logical-order pane of the bidi view work without one line of the Unicode Bidirectional
 * Algorithm. Once no bidi control remains in the string, no reordering can happen, so the browser
 * renders strict logical order for free.
 */
export function visibleText(s: string, opts: ChipOptions = {}): string {
  let out = '';
  for (const ch of s) {
    const cp = ch.codePointAt(0)!;
    if (opts.chipAsciiWhitespace && (cp === 0x09 || cp === 0x0a || cp === 0x0d)) {
      out += `${OPEN}${chipLabel(cp)}${CLOSE}`;
      continue;
    }
    out += needsChipping(cp) ? `${OPEN}${chipLabel(cp)}${CLOSE}` : ch;
  }
  return out;
}

/** The lossless form. A payload need not be text at all, and this is the only honest verbatim. */
export function hexDump(s: string): string {
  const bytes = new TextEncoder().encode(s);
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join(' ');
}

/** For `report.html`, which is written as a string. Applied AFTER `visibleText`, never instead. */
export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Untrusted text, ready to be written into an HTML file.
 *
 * The order matters and is not interchangeable: chip first so no live control reaches the output at
 * all, then escape so the chips and the surrounding prose cannot break out of the markup.
 */
export function safeHtmlText(s: string): string {
  return escapeHtml(visibleText(s, { chipAsciiWhitespace: true }));
}
